import { useQuery } from "@tanstack/react-query";
import { Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { referralsApi } from "@/api/referrals.ts";

function statusVariant(status: string): "success" | "info" | "muted" {
  if (status === "converted") return "success";
  if (status === "signed_up") return "info";
  return "muted";
}

export function AdminReferralsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals"],
    queryFn: () => referralsApi.adminList({ limit: 100 }),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const entries = data?.referrals ?? [];
  const total = data?.pagination?.total ?? 0;

  const converted = entries.filter((e) => e.status === "converted").length;
  const signedUp = entries.filter((e) => e.status === "signed_up").length;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center gap-2 mb-6">
        <Share2 className="w-5 h-5 text-surface-400" />
        <h1 className="text-xl font-semibold text-surface-900">Referrals</h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-surface-900">{total}</div>
            <div className="text-xs text-surface-500 mt-1">Total Referrals</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{signedUp}</div>
            <div className="text-xs text-surface-500 mt-1">Signed Up</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-emerald-600">{converted}</div>
            <div className="text-xs text-surface-500 mt-1">Converted</div>
          </CardContent>
        </Card>
      </div>

      {/* Referrals table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">All Referrals</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="py-8 text-center text-surface-400 text-sm">No referrals yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">Referrer</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">Referred</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-surface-500">Status</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-surface-500">Reward</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-surface-50 hover:bg-surface-25">
                    <td className="px-4 py-2.5 font-mono text-xs text-surface-600">{e.referrerEmail}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-surface-600">{e.referredEmail}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge variant={statusVariant(e.status)}>
                        {e.status === "converted" ? "Paid" : e.status === "signed_up" ? "Free" : "Churned"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right text-surface-700">
                      {e.status === "converted" ? `$${e.reward}` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-surface-500 text-xs">{new Date(e.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
